import React, { useState, useRef } from 'react';

const MarriageCalculatorSection = () => {
  const [childAgeToday, setChildAgeToday] = useState(2);
  const [childMarriageAge, setChildMarriageAge] = useState(25);
  const [weddingCost, setWeddingCost] = useState(1000000);
  const [annualSavings, setAnnualSavings] = useState(30000);
  const [existingSavings, setExistingSavings] = useState(100000);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [inflationRate, setInflationRate] = useState(6);
  const [result, setResult] = useState(null);
  
  const resultRef = useRef(null);

  const calculateChildMarriage = () => {
    const yearsToMarriage = childMarriageAge - childAgeToday;
    
    // Calculate future cost of wedding considering inflation
    const inflationFactor = Math.pow(1 + inflationRate / 100, yearsToMarriage);
    const futureWeddingCost = weddingCost * inflationFactor;
    
    // Calculate future value of existing savings
    const futureValueExistingSavings = existingSavings * Math.pow(1 + expectedReturn / 100, yearsToMarriage);
    
    // Calculate future value of annual savings (annuity)
    const futureValueAnnualSavings = annualSavings * (Math.pow(1 + expectedReturn / 100, yearsToMarriage) - 1) / (expectedReturn / 100);
    
    // Total accumulated amount
    const totalAccumulated = futureValueExistingSavings + futureValueAnnualSavings;
    
    // Calculate shortfall or surplus
    const shortfall = futureWeddingCost - totalAccumulated;
    
    // Calculate additional monthly SIP required if there's a shortfall
    let additionalMonthlySIP = 0;
    if (shortfall > 0) {
      const monthlyRate = expectedReturn / 100 / 12;
      const months = yearsToMarriage * 12;
      additionalMonthlySIP = shortfall * monthlyRate / (Math.pow(1 + monthlyRate, months) - 1);
    }
    
    const calculatedResult = {
      futureWeddingCost: Math.round(futureWeddingCost),
      futureValueExistingSavings: Math.round(futureValueExistingSavings),
      futureValueAnnualSavings: Math.round(futureValueAnnualSavings),
      totalAccumulated: Math.round(totalAccumulated),
      shortfall: Math.round(shortfall),
      additionalMonthlySIP: Math.round(Math.max(0, additionalMonthlySIP)),
      yearsToMarriage,
      isSufficient: shortfall <= 0
    };
    
    setResult(calculatedResult);
    
    // Auto-scroll to result section after a short delay
    setTimeout(() => {
      if (resultRef.current) {
        resultRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  };

  const styles = {
    marriageCalculatorContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2rem'
    },
    description: {
      color: '#6b7280',
      fontSize: '1rem',
      lineHeight: '1.6',
      margin: '0 0 2rem 0'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    label: {
      fontSize: '0.95rem',
      fontWeight: '600',
      color: '#374151',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    valueDisplay: {
      backgroundColor: '#374151',
      color: 'white',
      padding: '0.25rem 0.75rem',
      borderRadius: '15px',
      fontSize: '0.85rem',
      fontWeight: '600'
    },
    manualInput: {
      padding: '0.5rem 0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#374151',
      backgroundColor: 'white',
      width: '120px',
      textAlign: 'center',
      outline: 'none',
      transition: 'border-color 0.2s ease'
    },
    sliderContainer: {
      position: 'relative',
      margin: '1rem 0'
    },
    slider: {
      width: '100%',
      height: '6px',
      borderRadius: '3px',
      background: '#e5e7eb',
      outline: 'none',
      WebkitAppearance: 'none',
      appearance: 'none'
    },
    sliderLabels: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '0.75rem',
      color: '#9ca3af',
      marginTop: '0.5rem'
    },
    calculateButton: {
      backgroundColor: '#22c55e',
      color: 'white',
      border: 'none',
      padding: '0.75rem 2rem',
      borderRadius: '25px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      alignSelf: 'center',
      marginTop: '1rem'
    },
    // Result Section Styles
    resultSection: {
      marginTop: '3rem',
      padding: '0',
      backgroundColor: 'transparent'
    },
    resultHeader: {
      backgroundColor: '#c2e0f4',
      padding: '1rem 2rem',
      borderRadius: '8px 8px 0 0',
      border: '1px solid #a5c9dd',
      borderBottom: 'none'
    },
    resultHeaderText: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#1f2937',
      margin: '0'
    },
    resultTable: {
      width: '100%',
      borderCollapse: 'collapse',
      backgroundColor: 'white',
      border: '1px solid #d1d5db',
      borderRadius: '0 0 8px 8px',
      overflow: 'hidden'
    },
    resultRow: {
      borderBottom: '1px solid #e5e7eb'
    },
    resultCell: {
      padding: '1rem 2rem',
      fontSize: '0.95rem',
      color: '#374151',
      backgroundColor: '#f9fafb'
    },
    resultValueCell: {
      padding: '1rem 2rem',
      fontSize: '1rem',
      fontWeight: '600',
      color: '#1f2937',
      backgroundColor: '#ffffff',
      textAlign: 'right'
    },
    surplusText: {
      color: '#059669',
      fontWeight: '600'
    },
    recalculateButton: {
      backgroundColor: '#f3f4f6',
      color: '#374151',
      border: '1px solid #d1d5db',
      padding: '0.5rem 1.5rem',
      borderRadius: '6px',
      fontSize: '0.9rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginTop: '1rem'
    }
  };

  return (
    <>
      <style>{`
        .marriage-slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .marriage-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .marriage-calculate-btn:hover {
          background-color: #dc2626 !important;
        }
        
        .recalculate-btn:hover {
          background-color: #e5e7eb !important;
          border-color: #9ca3af !important;
        }
        
        .manual-input:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
        }
        
        @media (max-width: 768px) {
          .marriage-result-table {
            font-size: 0.85rem;
          }
          
          .marriage-result-cell,
          .marriage-result-value-cell {
            padding: 0.75rem 1rem !important;
          }
        }
      `}</style>
      
      <div style={styles.marriageCalculatorContent}>
        <p style={styles.description}>
          It helps you to plan for the expenditure to be incurred at the time of marriage of your child.
        </p>
        
        {/* Child's Age Today */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            What is age of your child today (Years)
            <input
              type="number"
              min="1"
              max="25"
              step="1"
              value={childAgeToday}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setChildAgeToday('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setChildAgeToday(Math.max(1, Math.min(25, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setChildAgeToday(2);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter age"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1"
              max="25"
              step="1"
              value={childAgeToday}
              onChange={(e) => setChildAgeToday(parseInt(e.target.value))}
              style={styles.slider}
              className="marriage-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1</span>
              <span>5</span>
              <span>9</span>
              <span>13</span>
              <span>17</span>
              <span>20</span>
              <span>23</span>
              <span>25</span>
            </div>
          </div>
        </div>

        {/* Child's Marriage Age */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            At what age your child will get married (Years)
            <input
              type="number"
              min="18"
              max="35"
              step="1"
              value={childMarriageAge}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setChildMarriageAge('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setChildMarriageAge(Math.max(18, Math.min(35, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setChildMarriageAge(25);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter age"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="18"
              max="35"
              step="1"
              value={childMarriageAge}
              onChange={(e) => setChildMarriageAge(parseInt(e.target.value))}
              style={styles.slider}
              className="marriage-slider"
            />
            <div style={styles.sliderLabels}>
              <span>18</span>
              <span>20</span>
              <span>21</span>
              <span>23</span>
              <span>25</span>
              <span>27</span>
              <span>30</span>
              <span>33</span>
              <span>35</span>
            </div>
          </div>
        </div>

        {/* Wedding Cost */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            How much expenditure is required for wedding at today's cost(Rs.)
            <input
              type="number"
              min="0"
              max="15000000"
              step="100000"
              value={weddingCost}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setWeddingCost('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setWeddingCost(Math.max(0, Math.min(15000000, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setWeddingCost(1000000);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter cost"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="0"
              max="15000000"
              step="100000"
              value={weddingCost}
              onChange={(e) => setWeddingCost(parseInt(e.target.value))}
              style={styles.slider}
              className="marriage-slider"
            />
            <div style={styles.sliderLabels}>
              <span>0</span>
              <span>37.5L</span>
              <span>75L</span>
              <span>1.12Cr</span>
              <span>1.5Cr</span>
            </div>
          </div>
        </div>

        {/* Annual Savings */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            How much is your Annual Savings presently (Rs.)
            <input
              type="number"
              min="0"
              max="1000000"
              step="10000"
              value={annualSavings}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setAnnualSavings('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setAnnualSavings(Math.max(0, Math.min(1000000, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setAnnualSavings(30000);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter savings"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="0"
              max="1000000"
              step="10000"
              value={annualSavings}
              onChange={(e) => setAnnualSavings(parseInt(e.target.value))}
              style={styles.slider}
              className="marriage-slider"
            />
            <div style={styles.sliderLabels}>
              <span>0</span>
              <span>2.5L</span>
              <span>5L</span>
              <span>7.5L</span>
              <span>10L</span>
            </div>
          </div>
        </div>

        {/* Existing Savings */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            How much you have already saved for marriage of your child (Rs.)
            <input
              type="number"
              min="0"
              max="10000000"
              step="100000"
              value={existingSavings}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setExistingSavings('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setExistingSavings(Math.max(0, Math.min(10000000, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setExistingSavings(100000);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter savings"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="0"
              max="10000000"
              step="100000"
              value={existingSavings}
              onChange={(e) => setExistingSavings(parseInt(e.target.value))}
              style={styles.slider}
              className="marriage-slider"
            />
            <div style={styles.sliderLabels}>
              <span>0</span>
              <span>25L</span>
              <span>50L</span>
              <span>75L</span>
              <span>1Cr</span>
            </div>
          </div>
        </div>

        {/* Expected Rate of Return */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Expected rate of return (%)
            <input
              type="number"
              min="1"
              max="25"
              step="0.5"
              value={expectedReturn}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setExpectedReturn('');
                } else {
                  const numValue = parseFloat(inputValue);
                  if (!isNaN(numValue)) {
                    setExpectedReturn(Math.max(1, Math.min(25, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                  setExpectedReturn(12);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter rate"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1"
              max="25"
              step="0.5"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(parseFloat(e.target.value))}
              style={styles.slider}
              className="marriage-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1</span>
              <span>3.4</span>
              <span>5.8</span>
              <span>8.2</span>
              <span>10.6</span>
              <span>13</span>
              <span>15.4</span>
              <span>17.8</span>
              <span>20.2</span>
              <span>22.6</span>
              <span>25</span>
            </div>
          </div>
        </div>

        {/* Expected Inflation Rate */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Expected Inflation rate (%)
            <input
              type="number"
              min="0"
              max="14"
              step="0.5"
              value={inflationRate}
              onChange={(e) => {
                const value = Math.max(0, Math.min(14, parseFloat(e.target.value) || 0));
                setInflationRate(value);
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter rate"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="0"
              max="14"
              step="0.5"
              value={inflationRate}
              onChange={(e) => setInflationRate(parseFloat(e.target.value))}
              style={styles.slider}
              className="marriage-slider"
            />
            <div style={styles.sliderLabels}>
              <span>0</span>
              <span>1.4</span>
              <span>2.8</span>
              <span>4.2</span>
              <span>5.6</span>
              <span>7</span>
              <span>8.4</span>
              <span>9.8</span>
              <span>11.2</span>
              <span>12.6</span>
              <span>14</span>
            </div>
          </div>
        </div>

        {/* Calculate Button */}
        <button
          style={styles.calculateButton}
          className="marriage-calculate-btn"
          onClick={calculateChildMarriage}
        >
          Calculate
        </button>

        {/* Results Section */}
        {result && (
          <div ref={resultRef} style={styles.resultSection}>
            <div style={styles.resultHeader}>
              <h3 style={styles.resultHeaderText}>Result</h3>
            </div>
            
            <table style={styles.resultTable} className="marriage-result-table">
              <tbody>
                <tr style={styles.resultRow}>
                  <td style={styles.resultCell}>
                    Inflation adjusted future cost of marriage of your child(Rs.)
                  </td>
                  <td style={{...styles.resultValueCell}} className="marriage-result-value-cell">
                    {result.futureWeddingCost.toLocaleString()}
                  </td>
                </tr>
                
                <tr style={styles.resultRow}>
                  <td style={styles.resultCell}>
                    Future value of your annual savings(Rs.)
                  </td>
                  <td style={{...styles.resultValueCell}} className="marriage-result-value-cell">
                    {result.futureValueAnnualSavings.toLocaleString()}
                  </td>
                </tr>
                
                <tr style={styles.resultRow}>
                  <td style={styles.resultCell}>
                    Your existing savings will grow to (Rs.)
                  </td>
                  <td style={{...styles.resultValueCell}} className="marriage-result-value-cell">
                    {result.futureValueExistingSavings.toLocaleString()}
                  </td>
                </tr>
                
                <tr style={styles.resultRow}>
                  <td style={styles.resultCell}>
                    Additional funds required to meet expenses(Rs.)
                  </td>
                  <td style={{...styles.resultValueCell, ...(result.isSufficient && styles.surplusText)}} className="marriage-result-value-cell">
                    {result.isSufficient ? 
                      "Not Required, You have surplus savings." : 
                      result.shortfall.toLocaleString()
                    }
                  </td>
                </tr>
              </tbody>
            </table>
            
            <button
              style={styles.recalculateButton}
              className="recalculate-btn"
              onClick={calculateChildMarriage}
            >
              Re-Calculate
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default MarriageCalculatorSection;